import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { iamApi } from '@/api/iam.api';
import { accountProfileToUserInfo } from '@/lib/mapIamProfile';
import { useAuthStore } from '@/store/auth.store';

type CallbackStatus = 'processing' | 'success' | 'error';

export default function SAMLCallback() {
  const navigate = useNavigate();
  const { setToken, setUser } = useAuthStore();
  const startedRef = useRef(false);
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [message, setMessage] = useState('正在处理 SSO 回调...');

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const finishLogin = async (accessToken: string) => {
      setToken(accessToken);
      const profile = await iamApi.getAccountProfile(accessToken);
      setUser(accountProfileToUserInfo(profile, profile.account?.name || profile.user?.name || ''));
      setStatus('success');
      setMessage('登录成功，正在跳转...');
      setTimeout(() => navigate('/dashboard', { replace: true }), 800);
    };

    const handleCallback = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        if (token) {
          // Remove credentials before profile requests or navigation can leak the URL.
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete('token');
          cleanUrl.searchParams.delete('refresh_token');
          window.history.replaceState(null, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
          await finishLogin(token);
          return;
        }

        const samlResponse = params.get('SAMLResponse');
        if (samlResponse) {
          const tokenData = await iamApi.submitSAMLResponse(samlResponse);
          await finishLogin(tokenData.access_token);
          return;
        }

        setStatus('error');
        setMessage('未接收到 SSO 回调参数，请重新登录');
        setTimeout(() => navigate('/login/account', { replace: true }), 1800);
      } catch (error) {
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'SSO 登录失败');
        setTimeout(() => navigate('/login/account', { replace: true }), 1800);
      }
    };

    handleCallback();
  }, [navigate, setToken, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="text-center">
        {status === 'processing' && (
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
        )}
        {status === 'success' && (
          <div className="rounded-full h-12 w-12 bg-green-100 flex items-center justify-center mx-auto text-green-600">
            ✓
          </div>
        )}
        {status === 'error' && (
          <div className="rounded-full h-12 w-12 bg-red-100 flex items-center justify-center mx-auto text-red-600">
            ✕
          </div>
        )}
        <p className="mt-4 text-gray-600">{message}</p>
      </div>
    </div>
  );
}
